import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfigProvider, theme as antdTheme, Select as AntdSelect, message } from "antd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/inputForm";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { getUnidades } from "@/services/unidade";
import { getTurma } from "@/services/turma";
import { getAllEspecificas } from "@/services/especifica";
import { getGradeHorariaById, saveGradeHoraria } from "@/services/gradeHoraria";

// ─── Types ────────────────────────────────────────────────────────────────────

const GRUPOS = ["Manha", "Tarde", "Noite"] as const;
type Grupo = (typeof GRUPOS)[number];

const DIAS = ["Segunda", "Terca", "Quarta", "Quinta", "Sexta", "Sabado"] as const;
type Dia = (typeof DIAS)[number];

const DIA_LABELS: Record<Dia, string> = {
  Segunda: "Segunda",
  Terca: "Terça",
  Quarta: "Quarta",
  Quinta: "Quinta",
  Sexta: "Sexta",
  Sabado: "Sábado",
};

const GRUPO_LABELS: Record<Grupo, string> = {
  Manha: "Manhã",
  Tarde: "Tarde",
  Noite: "Noite",
};

const ORDENS_POR_GRUPO: Record<Grupo, number[]> = {
  Manha: [1, 2, 3],
  Tarde: [4, 5, 6],
  Noite: [7, 8, 9],
};

interface HorarioState {
  id?: number;
  grupo: Grupo;
  ordem: number;
  horaInicio: string;
  horaFim: string;
  aulas: Record<Dia, number[]>; // dia → especificaIds
}

function formatTimeInput(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function isValidTime(value: string): boolean {
  if (value.length !== 5) return false;
  const [hh, mm] = value.split(":");
  const h = parseInt(hh, 10);
  const m = parseInt(mm, 10);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

function isStartBeforeEnd(start: string, end: string): boolean {
  if (!isValidTime(start) || !isValidTime(end)) return true; // só valida quando ambos válidos
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return sh * 60 + sm < eh * 60 + em;
}

function defaultHorarios(): HorarioState[] {
  return GRUPOS.flatMap((grupo) =>
    ORDENS_POR_GRUPO[grupo].map((ordem) => ({
      grupo,
      ordem,
      horaInicio: "",
      horaFim: "",
      aulas: Object.fromEntries(DIAS.map((d) => [d, [] as number[]])) as unknown as Record<Dia, number[]>,
    }))
  );
}

// ─── Multi-select cell ────────────────────────────────────────────────────────

interface MultiSelectCellProps {
  selected: number[];
  options: { id: number; nome: string }[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

function MultiSelectCell({ selected, options, onChange, disabled }: MultiSelectCellProps) {
  const toggle = (id: number) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const label =
    selected.length === 0
      ? "—"
      : options
          .filter((o) => selected.includes(o.id))
          .map((o) => o.nome)
          .join(", ");

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        className="w-full min-h-[2rem] text-left text-xs px-1 py-0.5 rounded border border-gray-700 bg-gray-900 text-gray-600 cursor-not-allowed truncate"
        title="Preencha o horário de início e fim para habilitar"
      >
        —
      </button>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[2rem] text-left text-xs px-1 py-0.5 rounded border border-gray-600 bg-gray-800 text-gray-200 hover:border-gray-400 truncate"
          title={label}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 max-h-64 overflow-y-auto p-2 bg-gray-900 border-gray-700">
        {options.length === 0 ? (
          <span className="text-xs text-gray-500">Nenhuma específica</span>
        ) : (
          options.map((opt) => (
            <div key={opt.id} className="flex items-center gap-2 py-1">
              <Checkbox
                id={`opt-${opt.id}`}
                checked={selected.includes(opt.id)}
                onCheckedChange={() => toggle(opt.id)}
              />
              <label htmlFor={`opt-${opt.id}`} className="text-xs text-gray-200 cursor-pointer">
                {opt.nome}
              </label>
            </div>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function CadastroGradeHoraria() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [unidades, setUnidades] = useState<any[]>([]);
  const [turmas, setTurmas] = useState<any[]>([]);
  const [especificas, setEspecificas] = useState<{ id: number; nome: string }[]>([]);

  const [unidadeId, setUnidadeId] = useState<number | undefined>();
  const [turmaId, setTurmaId] = useState<number | undefined>();
  const [ativo, setAtivo] = useState(true);
  const [periodoDatas, setPeriodoDatas] = useState<string>("");
  const [horarios, setHorarios] = useState<HorarioState[]>(defaultHorarios());

  // ── Load reference data ────────────────────────────────────────────────────
  useEffect(() => {
    getUnidades().then((res) => {
      if (res.status === 200) setUnidades(res.data.filter((u: any) => u.ativo));
    });
    getTurma().then((res) => {
      if (res.status === 200) setTurmas(res.data.filter((t: any) => t.ativo));
    });
  }, []);

  // ── Load especificas when turma changes ───────────────────────────────────
  useEffect(() => {
    if (turmaId) {
      getAllEspecificas({ turmaId: String(turmaId) }).then((res) => {
        if (res.status === 200)
          setEspecificas(
            res.data
              .filter((e: any) => e.ativo)
              .map((e: any) => ({ id: e.id, nome: e.nome }))
          );
        else setEspecificas([]);
      });

      const turma = turmas.find((t) => t.id === turmaId);
      if (turma?.periodoLetivo) {
        const ini = new Date(turma.periodoLetivo.dataInicio).toLocaleDateString("pt-BR");
        const fim = new Date(turma.periodoLetivo.dataFim).toLocaleDateString("pt-BR");
        setPeriodoDatas(`${ini} a ${fim}`);
      } else {
        setPeriodoDatas("");
      }
    } else {
      setEspecificas([]);
      setPeriodoDatas("");
    }
  }, [turmaId, turmas]);

  // ── Load edit data ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isEdit && id) {
      getGradeHorariaById(id).then((res) => {
        if (res.status === 200) {
          const grade = res.data;
          setUnidadeId(grade.unidadeId);
          setTurmaId(grade.turmaId);
          setAtivo(grade.ativo);

          const loaded = defaultHorarios();
          grade.horarios?.forEach((h: any) => {
            const idx = loaded.findIndex(
              (l) => l.grupo === h.grupo && l.ordem === h.ordem
            );
            if (idx !== -1) {
              loaded[idx].id = h.id;
              loaded[idx].horaInicio = h.horaInicio;
              loaded[idx].horaFim = h.horaFim;
              h.aulas?.forEach((a: any) => {
                const dia = a.diaSemana as Dia;
                if (!loaded[idx].aulas[dia]) loaded[idx].aulas[dia] = [];
                if (!loaded[idx].aulas[dia].includes(a.especificaId)) {
                  loaded[idx].aulas[dia].push(a.especificaId);
                }
              });
            }
          });
          setHorarios(loaded);
        }
      });
    }
  }, [id, isEdit]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const updateHorario = (
    ordem: number,
    field: "horaInicio" | "horaFim",
    value: string
  ) => {
    setHorarios((prev) =>
      prev.map((h) => {
        if (h.ordem !== ordem) return h;
        const updated = { ...h, [field]: value };
        // Se ficou sem horário completo, limpa as aulas deste slot
        if (!updated.horaInicio || !updated.horaFim) {
          const emptyAulas = Object.fromEntries(
            DIAS.map((d) => [d, [] as number[]])
          ) as unknown as Record<Dia, number[]>;
          return { ...updated, aulas: emptyAulas };
        }
        return updated;
      })
    );
  };

  const updateAulas = (ordem: number, dia: Dia, ids: number[]) => {
    setHorarios((prev) =>
      prev.map((h) =>
        h.ordem === ordem ? { ...h, aulas: { ...h.aulas, [dia]: ids } } : h
      )
    );
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turmaId || !unidadeId) {
      message.error("Selecione a unidade e a turma.");
      return;
    }

    const slotComAulasSemHorario = horarios.find(
      (h) => DIAS.some((dia) => (h.aulas[dia] ?? []).length > 0) && (!h.horaInicio || !h.horaFim)
    );
    if (slotComAulasSemHorario) {
      message.error("Preencha o horário de início e fim em todos os slots que possuem específicas selecionadas.");
      return;
    }

    const temSlotCompletoComAula = horarios.some(
      (h) =>
        h.horaInicio &&
        h.horaFim &&
        DIAS.some((dia) => (h.aulas[dia] ?? []).length > 0)
    );
    if (!temSlotCompletoComAula) {
      message.error("Informe ao menos um horário completo (início e fim) com uma específica selecionada.");
      return;
    }

    const slotHorarioInvalido = horarios.find(
      (h) =>
        (h.horaInicio && !isValidTime(h.horaInicio)) ||
        (h.horaFim && !isValidTime(h.horaFim))
    );
    if (slotHorarioInvalido) {
      message.error("Existem horários inválidos. Use o formato HH:MM com horas entre 00-23 e minutos entre 00-59.");
      return;
    }

    const slotOrdemInvalida = horarios.find(
      (h) => h.horaInicio && h.horaFim && !isStartBeforeEnd(h.horaInicio, h.horaFim)
    );
    if (slotOrdemInvalida) {
      message.error("O horário de início deve ser anterior ao horário de fim.");
      return;
    }

    const payload = {
      id: id ? Number(id) : undefined,
      turmaId,
      unidadeId,
      ativo,
      horarios: horarios.map((h) => ({
        id: h.id,
        grupo: h.grupo,
        ordem: h.ordem,
        horaInicio: h.horaInicio,
        horaFim: h.horaFim,
        aulas: DIAS.map((dia) => ({
          diaSemana: dia,
          especificaIds: h.aulas[dia] ?? [],
        })).filter((a) => a.especificaIds.length > 0),
      })),
    };

    try {
      const res = await saveGradeHoraria(payload);
      if (res.status === 200 || res.status === 201) {
        message.success("Grade horária salva com sucesso!");
        navigate("/gradehoraria");
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.errors?.join(", ") ||
        err?.response?.data ||
        "Erro ao salvar grade horária.";
      message.error(String(errorMsg));
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-full mx-auto p-8">
      <form onSubmit={handleSubmit}>
        <div className="mb-8">
          <span className="text-white text-2xl">
            {isEdit ? "Editar Grade Horária" : "Cadastro de Grade Horária"}
          </span>
        </div>

        <ConfigProvider theme={{ algorithm: [antdTheme.compactAlgorithm, antdTheme.darkAlgorithm] }}>
          {/* ── Selects de unidade e turma ── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="flex flex-col gap-1">
              <label className="text-white text-sm">Unidade *</label>
              <AntdSelect
                placeholder="Selecione a unidade"
                value={unidadeId}
                onChange={(val) => setUnidadeId(val)}
                options={unidades.map((u) => ({ value: u.id, label: u.nome }))}
                style={{ width: "100%" }}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white text-sm">Turma *</label>
              <AntdSelect
                placeholder="Selecione a turma"
                value={turmaId}
                onChange={(val) => setTurmaId(val)}
                options={turmas.map((t) => ({ value: t.id, label: t.nome }))}
                style={{ width: "100%" }}
              />
            </div>

            <div className="flex flex-col gap-1 justify-end">
              {periodoDatas && (
                <span className="text-gray-300 text-sm">
                  Período: <strong>{periodoDatas}</strong>
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            <Checkbox
              id="ativo"
              checked={ativo}
              onCheckedChange={(v) => setAtivo(Boolean(v))}
            />
            <label htmlFor="ativo" className="text-white text-sm cursor-pointer">
              Ativo
            </label>
          </div>

          {/* ── Grid de horários ── */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs text-white">
              <thead>
                <tr className="bg-gray-700">
                  <th className="border border-gray-600 px-2 py-2 text-left w-32">Horário</th>
                  {DIAS.map((dia) => (
                    <th key={dia} className="border border-gray-600 px-2 py-2 text-center min-w-[120px]">
                      {DIA_LABELS[dia]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {GRUPOS.map((grupo) => (
                  <>
                    <tr key={`header-${grupo}`} className="bg-gray-800">
                      <td
                        colSpan={DIAS.length + 1}
                        className="border border-gray-600 px-2 py-1 font-semibold text-yellow-400 text-xs"
                      >
                        {GRUPO_LABELS[grupo]}
                      </td>
                    </tr>
                    {ORDENS_POR_GRUPO[grupo].map((ordem) => {
                      const horario = horarios.find((h) => h.ordem === ordem)!;
                      return (
                        <tr key={ordem} className="bg-gray-900 hover:bg-gray-800">
                          <td className="border border-gray-600 px-1 py-1">
                            <div className="flex items-center gap-1">
                              <Input
                                type="text"
                                placeholder="00:00"
                                value={horario.horaInicio}
                                onChange={(e) =>
                                  updateHorario(ordem, "horaInicio", formatTimeInput(e.target.value))
                                }
                                maxLength={5}
                                className={`w-14 text-xs text-center ${
                                  horario.horaInicio.length === 5 && !isValidTime(horario.horaInicio)
                                    ? "border-red-500 focus:border-red-500"
                                    : ""
                                }`}
                              />
                              <span className="text-gray-400">–</span>
                              <Input
                                type="text"
                                placeholder="00:00"
                                value={horario.horaFim}
                                onChange={(e) =>
                                  updateHorario(ordem, "horaFim", formatTimeInput(e.target.value))
                                }
                                maxLength={5}
                                className={`w-14 text-xs text-center ${
                                  (horario.horaFim.length === 5 && !isValidTime(horario.horaFim)) ||
                                  (horario.horaInicio.length === 5 && horario.horaFim.length === 5 &&
                                    isValidTime(horario.horaInicio) && isValidTime(horario.horaFim) &&
                                    !isStartBeforeEnd(horario.horaInicio, horario.horaFim))
                                    ? "border-red-500 focus:border-red-500"
                                    : ""
                                }`}
                              />
                            </div>
                          </td>
                          {DIAS.map((dia) => (
                            <td key={dia} className="border border-gray-600 px-1 py-1">
                              <MultiSelectCell
                                selected={horario.aulas[dia] ?? []}
                                options={especificas}
                                onChange={(ids) => updateAulas(ordem, dia, ids)}
                                disabled={!horario.horaInicio || !horario.horaFim}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </ConfigProvider>

        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            variant="destructive"
            type="button"
            onClick={() => navigate("/gradehoraria")}
          >
            Cancelar
          </Button>
          <Button type="submit">Salvar</Button>
        </div>
      </form>
    </div>
  );
}
